// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import AlertSuccess from "@stoneage/XDL/Icons/AlertSuccess";
import IconClear from "@stoneage/XDL/Icons/IconClear";
import IconNotificationError from "@stoneage/XDL/Icons/IconNotificationError";
import IconNotificationInfo from "@stoneage/XDL/Icons/IconNotificationInfo";
import IconNotificationWarning from "@stoneage/XDL/Icons/IconNotificationWarning";
import { fontConfig } from "@stoneage/XDL/constants";
import { color, getSystemColor, mergeWithCss } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends TypographyProps {
    type?: "success" | "danger" | "warning" | "info";
    showClose?: boolean;
    onClose?: () => void;
}

type Props = OwnProps;

const base64Icons = {
    success: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-.527-6.152-.598.597-.598-.597-2.812-2.813L5.66 8.09l2.215 2.215L12.34 5.84l1.195 1.195-5.062 5.063Z'/%3E%3C/svg%3E`,
    danger: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12.442 7l-.598.598-1.652 1.652 1.652 1.652.598.598-1.192 1.192-.598-.598L9 10.442l-1.652 1.652-.598.598L5.558 11.5l.598-.598L7.808 9.25 6.156 7.598 5.558 7 6.75 5.808l.598.598L9 8.058l1.652-1.652.598-.598L12.442 7Z'/%3E%3C/svg%3E`,
    warning: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm.844-13.5v5.625H8.156V4.75h1.688Zm-1.688 8.437V11.5h1.688v1.687H8.156Z'/%3E%3C/svg%3E`,
    info: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-1.406-6.188h.843v-2.25H6.75V8.126h3.375V12.063h1.125v1.687h-4.5v-1.688h.844ZM10.125 7h-2.25V4.75h2.25V7Z'/%3E%3C/svg%3E`,
};

const getNotifyIcons = (type: OwnProps["type"], useBase64 = true) => {
    const iconColor = getSystemColor(`${type}.main`, 1, true).replace("#", "%23");
    switch (type) {
        case "success":
            return useBase64 ? (
                base64Icons["success"](iconColor)
            ) : (
                <Icon src={AlertSuccess} color={iconColor} size={16} />
            );
        case "danger":
            return useBase64 ? (
                base64Icons["danger"](iconColor)
            ) : (
                <Icon src={IconNotificationError} color={iconColor} size={16} />
            );
        case "warning":
            return useBase64 ? (
                base64Icons["warning"](iconColor)
            ) : (
                <Icon src={IconNotificationWarning} color={iconColor} size={16} />
            );
        case "info":
            return useBase64 ? (
                base64Icons["info"](iconColor)
            ) : (
                <Icon src={IconNotificationInfo} color={iconColor} size={16} />
            );
    }
};

export const getNotifyStyle = (type: OwnProps["type"], useBase64 = true): TypographyProps => {
    return {
        role: "status",
        "aria-live": "polite",
        "data-test-selector": `notify-${type}`,
        css: css`
            position: relative;
            display: flex;
            padding: 8px 12px;
            text-align: left;
            border: 1px solid ${color(`${type}.main`)};
            background-color: ${color(`${type}.light`)};
            width: 100%;
            max-width: 543px;
            min-width: 200px;
            margin: 0 0 16px;
            font-size: ${fontConfig.small.size}px;
            line-height: ${fontConfig.small.lineHeight};
            font-weight: ${fontConfig.weight.regular};
            color: ${color("text.main")};
            letter-spacing: 0;

            .notify-left-side {
                display: flex;
                align-items: flex-start;
            }
            .notify-icon-wrapper {
                &:empty {
                    display: none;
                }
                display: flex;
                margin-right: 12px;
                margin-top: 2px;
            }
            .notify-close-wrapper {
                cursor: pointer;
                margin-left: 18px;
                ${IconWrapper} {
                    color: ${color("text.main")};
                    &:hover {
                        color: ${color("common.ui9")};
                    }
                }
            }
            .notify-content-wrapper {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
            }
            @media print {
                -webkit-print-color-adjust: exact !important; /* Chrome, Safari 6 – 15.3, Edge */
                color-adjust: exact !important; /* Firefox 48 – 96 */
                print-color-adjust: exact !important; /* Firefox 97+, Safari 15.4+ */
            }

            ${useBase64
                ? css`
                      .notify-close-wrapper {
                          margin-left: auto;
                      }
                      &:before {
                          height: 18px;
                          width: 18px;
                          min-width: 18px;
                          margin-right: 8px;
                          content: " ";
                          margin-top: 2px;
                          background-repeat: no-repeat;
                          background-image: url("${getNotifyIcons(type, useBase64) as string}");
                      }
                  `
                : css`
                      justify-content: space-between;
                  `}
        `,
    };
};

const Notify: FC<Props> = ({ type, className, children, css: extendedCssStyle, showClose = false, onClose }) => {
    const [styles] = React.useState(() => mergeWithCss(getNotifyStyle(type, false), { css: extendedCssStyle }));
    const [visible, setVisibility] = React.useState(true);

    if (!React.Children.count(children) || !visible) {
        return null;
    }

    const handleCloseNotify = () => {
        onClose?.();
        setVisibility(false);
    };

    const classNames = ["notify", type, className || ""].join(" ").trim();
    return (
        <Typography {...styles} className={classNames}>
            <Typography className="notify-left-side">
                <Typography className="notify-icon-wrapper">{getNotifyIcons(type, false)}</Typography>
                <Typography className="notify-content-wrapper">{children}</Typography>
            </Typography>
            {showClose && (
                <Typography
                    className="notify-close-wrapper"
                    onClick={handleCloseNotify}
                    role="button"
                    aria-label="Close"
                >
                    <Icon src={IconClear} size={12} />
                </Typography>
            )}
        </Typography>
    );
};

export default Notify;
